import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import './RequestForm.css';

const MAX_FIELD_LENGTH = 200;
const SUBMIT_COOLDOWN_MS = 30000;

const sanitize = (str) => str.replace(/[<>{}]/g, '').trim().slice(0, MAX_FIELD_LENGTH);

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isValidPhone = (phone) => /^[\d\s()+-]{7,20}$/.test(phone);

const RequestForm = ({ preSelectedService = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    service: '',
    city: '',
    email: '',
    phone: ''
  });
  const [honeypot, setHoneypot] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [lastSubmitTime, setLastSubmitTime] = useState(0);

  // Update service when preSelectedService changes
  useEffect(() => {
    if (preSelectedService) {
      setFormData(prev => ({ ...prev, service: preSelectedService }));
    }
  }, [preSelectedService]);

  const services = [
    'Cable Management',
    'Network Setup',
    'Tech and TV Mounting',
    'System Setup',
    'Cloud or Disk Backup',
    'Hardware Upgrade',
    'Software Installation',
    'System Optimization',
    'Tech Support',
    'Tech Consultation',
    'Other'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (honeypot) return;

    const now = Date.now();
    if (now - lastSubmitTime < SUBMIT_COOLDOWN_MS) {
      const secondsLeft = Math.ceil((SUBMIT_COOLDOWN_MS - (now - lastSubmitTime)) / 1000);
      setError(`Please wait ${secondsLeft} seconds before submitting again`);
      return;
    }

    const cleanName = sanitize(formData.name);
    const cleanCity = sanitize(formData.city);
    const cleanEmail = formData.email ? sanitize(formData.email) : '';
    const cleanPhone = formData.phone ? sanitize(formData.phone) : '';

    if (!cleanEmail && !cleanPhone) {
      setError('Please provide either an email address or phone number');
      return;
    }

    if (!cleanName || !formData.service || !cleanCity) {
      setError('Please fill out all required fields');
      return;
    }

    if (cleanEmail && !isValidEmail(cleanEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (cleanPhone && !isValidPhone(cleanPhone)) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await addDoc(collection(db, 'requests'), {
        name: cleanName,
        service: formData.service,
        city: cleanCity,
        email: cleanEmail || null,
        phone: cleanPhone || null,
        status: 'new',
        createdAt: serverTimestamp()
      });

      setLastSubmitTime(Date.now());

      setSubmitted(true);
      setFormData({
        name: '',
        service: '',
        city: '',
        email: '',
        phone: ''
      });
    } catch (err) {
      console.error('Error submitting request:', err);
      setError('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="success-message">
        <div className="success-icon">✓</div>
        <h2>Request Submitted Successfully!</h2>
        <p>Thank you for contacting MTS Tech Solutions. We'll get back to you shortly.</p>
        <button onClick={() => setSubmitted(false)} className="btn-secondary">
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <div className="request-form-container">
      <div className="form-header">
        <h1>MTS Tech Solutions</h1>
        <p>Request Professional Tech Support</p>
      </div>

      <form onSubmit={handleSubmit} className="request-form" autoComplete="off">
        {error && <div className="error-message">{error}</div>}

        {/* honeypot section, hidden from users and only for bots*/}
        <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            type="text"
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="name">Full Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter your name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="service">Service Needed *</label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            required
          >
            <option value="">Select a service...</option>
            {services.map((service) => (
              <option key={service} value={service}>
                {service}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="city">City *</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
            placeholder="Enter your city"
          />
        </div>

        <div className="contact-section">
          <p className="contact-label">Contact Information * (Provide at least one)</p>
          
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 123-4567"
            />
          </div>
        </div>

        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Submitting...' : 'Submit Request'}
        </button>
      </form>
    </div>
  );
};

export default RequestForm;
